
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface Rule {
  id: number;
  pattern: string;
  response: string;
  priority: number;
  language: string;
  created_at?: string;
}

export const RulesManagement: React.FC = () => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [newRule, setNewRule] = useState<Partial<Rule>>({
    pattern: '',
    response: '',
    priority: 0,
    language: 'en',
  });
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Fetch rules from database
  useEffect(() => {
    const fetchRules = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('assistant_rules')
          .select('*')
          .order('priority', { ascending: false });

        if (error) {
          throw error;
        }

        setRules(data || []);
      } catch (error) {
        console.error('Error fetching rules:', error);
        toast.error('Failed to fetch rules');
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  // Filter rules based on search query
  const filteredRules = rules.filter(
    rule =>
      rule.pattern.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.response.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Save a new rule
  const handleSaveNewRule = async () => {
    try {
      if (!newRule.pattern || !newRule.response) {
        toast.error('Pattern and response are required');
        return;
      }

      // Validate regex pattern
      try {
        new RegExp(newRule.pattern);
      } catch (e) {
        toast.error('Invalid regex pattern');
        return;
      }

      const { data, error } = await supabase
        .from('assistant_rules')
        .insert({
          pattern: newRule.pattern,
          response: newRule.response,
          priority: newRule.priority || 0,
          language: newRule.language || 'en',
        })
        .select();

      if (error) {
        throw error;
      }

      setRules([...rules, data[0]]);
      setNewRule({
        pattern: '',
        response: '',
        priority: 0,
        language: 'en',
      });
      setIsAddingNew(false);
      toast.success('Rule added successfully');
    } catch (error) {
      console.error('Error adding rule:', error);
      toast.error('Failed to add rule');
    }
  };

  // Update an existing rule
  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      if (!editingRule.pattern || !editingRule.response) {
        toast.error('Pattern and response are required');
        return;
      }

      // Validate regex pattern
      try {
        new RegExp(editingRule.pattern);
      } catch (e) {
        toast.error('Invalid regex pattern');
        return;
      }

      const { error } = await supabase
        .from('assistant_rules')
        .update({
          pattern: editingRule.pattern,
          response: editingRule.response,
          priority: editingRule.priority,
          language: editingRule.language,
        })
        .eq('id', editingRule.id);

      if (error) {
        throw error;
      }

      setRules(
        rules.map(rule =>
          rule.id === editingRule.id ? { ...editingRule } : rule
        )
      );
      setEditingRule(null);
      toast.success('Rule updated successfully');
    } catch (error) {
      console.error('Error updating rule:', error);
      toast.error('Failed to update rule');
    }
  };

  // Delete a rule
  const handleDeleteRule = async (id: number) => {
    try {
      const { error } = await supabase
        .from('assistant_rules')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setRules(rules.filter(rule => rule.id !== id));
      toast.success('Rule deleted successfully');
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  // Test the rule pattern against a sample text
  const testPattern = (pattern: string, testText: string) => {
    try {
      const regex = new RegExp(pattern, 'i');
      return regex.test(testText);
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <Input
          placeholder="Search rules..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
        <Button onClick={() => setIsAddingNew(true)} disabled={isAddingNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Rule
        </Button>
      </div>

      {isAddingNew && (
        <Card className="mb-6 border-dashed">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Pattern (Regex)
                  </label>
                  <Input
                    value={newRule.pattern}
                    onChange={e =>
                      setNewRule({ ...newRule, pattern: e.target.value })
                    }
                    placeholder="Regular expression pattern"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Priority
                    </label>
                    <Input
                      type="number"
                      value={newRule.priority}
                      onChange={e =>
                        setNewRule({
                          ...newRule,
                          priority: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Language
                    </label>
                    <Select
                      value={newRule.language}
                      onValueChange={value =>
                        setNewRule({ ...newRule, language: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="vi">Vietnamese</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                        <SelectItem value="th">Thai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Response
                </label>
                <Textarea
                  rows={5}
                  value={newRule.response}
                  onChange={e =>
                    setNewRule({ ...newRule, response: e.target.value })
                  }
                  placeholder="Response text (can include SUGGESTED_QUESTIONS section)"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsAddingNew(false)}
                >
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
                <Button onClick={handleSaveNewRule}>
                  <Save className="mr-2 h-4 w-4" /> Save Rule
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-4">Loading rules...</div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          {searchQuery
            ? 'No rules match your search'
            : 'No rules found. Add your first rule above.'}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pattern</TableHead>
                <TableHead>Response</TableHead>
                <TableHead className="w-24">Priority</TableHead>
                <TableHead className="w-24">Language</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map(rule => (
                <TableRow key={rule.id}>
                  <TableCell className="font-mono">
                    {editingRule?.id === rule.id ? (
                      <Input
                        value={editingRule.pattern}
                        onChange={e =>
                          setEditingRule({
                            ...editingRule,
                            pattern: e.target.value,
                          })
                        }
                      />
                    ) : (
                      rule.pattern
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRule?.id === rule.id ? (
                      <Textarea
                        value={editingRule.response}
                        onChange={e =>
                          setEditingRule({
                            ...editingRule,
                            response: e.target.value,
                          })
                        }
                        rows={3}
                      />
                    ) : (
                      <div className="max-h-20 overflow-y-auto">
                        {rule.response.length > 100
                          ? `${rule.response.substring(0, 100)}...`
                          : rule.response}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRule?.id === rule.id ? (
                      <Input
                        type="number"
                        value={editingRule.priority}
                        onChange={e =>
                          setEditingRule({
                            ...editingRule,
                            priority: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    ) : (
                      <Badge variant="outline">{rule.priority}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRule?.id === rule.id ? (
                      <Select
                        value={editingRule.language}
                        onValueChange={value =>
                          setEditingRule({
                            ...editingRule,
                            language: value,
                          })
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="vi">Vietnamese</SelectItem>
                          <SelectItem value="zh">Chinese</SelectItem>
                          <SelectItem value="ja">Japanese</SelectItem>
                          <SelectItem value="ko">Korean</SelectItem>
                          <SelectItem value="th">Thai</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline">{rule.language}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRule?.id === rule.id ? (
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleUpdateRule}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingRule(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingRule({ ...rule })}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Rule
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this rule? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteRule(rule.id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
